// src/MainApp.js

import React, { useState, useEffect, Suspense, lazy, useCallback, useRef } from 'react';
import { Routes, Route, Navigate } from "react-router-dom";
import { useSubscription, PRO_ONLY_FEATURES } from './context/SubscriptionContext';
import { ProFeaturePage } from './components/ProFeatureGate';

// Lazy load pages for better initial bundle size
const Dashboard = lazy(() => import('./pages/Dashboard'));
const InventoryPage = lazy(() => import('./pages/InventoryPage'));
const SalesPage = lazy(() => import('./pages/SalesPage'));
const ExpensesPage = lazy(() => import('./pages/ExpensesPage'));
// RenewalsPage archived — renewals now accessed via drawer in ExpensesPage
// const RenewalsPage = lazy(() => import('./pages/RenewalsPage'));
const ProductScannerPage = lazy(() => import('./pages/ProductScannerPage'));
// const PackagesPage = lazy(() => import('./pages/PackagesPage')); // Archived — merged into OrdersTrackingPage
const Reports = lazy(() => import('./pages/Reports'));
const ViewItemPage = lazy(() => import('./pages/ViewItemPage'));
const GlobalProductsAdmin = lazy(() => import('./pages/GlobalProductsAdmin'));
const AdminSettingsPage = lazy(() => import('./pages/AdminSettingsPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const VaultPage = lazy(() => import('./pages/VaultPage'));
const ArchivesPage = lazy(() => import('./pages/ArchivesPage'));
const BuyersPage = lazy(() => import('./pages/BuyersPage'));
// const EmailScraperPage = lazy(() => import('./pages/EmailScraperPage')); // Archived — merged into OrdersTrackingPage
const OrdersTrackingPage = lazy(() => import('./pages/OrdersTrackingPage'));
// eBay integration disabled - uncomment when ready for production
// const ListingsPage = lazy(() => import('./pages/ListingsPage'));
// const EbayCallbackPage = lazy(() => import('./pages/EbayCallbackPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
// Dev/tooling page — live-tune the ambient body gradient.
// Not exposed in the navbar; reach it via direct URL /gradient-playground.
const GradientPlaygroundPage = lazy(() => import('./pages/GradientPlaygroundPage'));

// Protected route wrapper - redirects to account if subscription is invalid
const ProtectedRoute = ({ children }) => {
  const { subscriptionStatus } = useSubscription();

  // If user needs to resubscribe, redirect to account page
  if (subscriptionStatus.needsResubscribe) {
    return <Navigate to="/account" replace />;
  }

  return children;
};

// Page loading fallback.
//
// Pinned to var(--color-bg) and made min-h-screen so it fully covers
// the content area in the same color as the surrounding pages. Before
// this it had no background and only min-h-[50vh] — first-time route
// loads briefly showed the bare layout (different shade) which read
// as a "page reload flash" through the tour overlay during the new-user
// tour. Solid bg + full-height kills that visible gap.
const PageLoader = () => (
  <div
    className="flex items-center justify-center min-h-screen"
    style={{ backgroundColor: 'var(--color-bg)' }}
  >
    <div className="flex flex-col items-center gap-3">
      <div className="h-8 w-8 animate-spin rounded-full border-3 border-[var(--color-border)] border-t-[rgb(var(--accent-primary))]" />
      <p className="text-sm text-[var(--color-text-muted)]">Loading...</p>
    </div>
  </div>
);

// Import Shared Components
import Navbar from './components/Navbar';
import MobileNavBar from './components/MobileNavBar';
import Footer from './components/Footer';
// Update notes modal removed - announcements now on Discord
// import UpdateNotesModal from './components/UpdateNotesModal';
// import { useUpdateNotes } from './hooks/useUpdateNotes';
import GlobalSearchModal, { useGlobalSearch } from './components/GlobalSearch';
import KeyboardShortcutsModal, { useKeyboardShortcutsModal } from './components/KeyboardShortcutsModal';
import UpgradeBanner from './components/UpgradeBanner';
import WelcomeModal from './components/WelcomeModal';
import MobileQuickActions from './components/MobileQuickActions';
import { TourProvider } from './context/TourContext';
import { PrivacyProvider } from './context/PrivacyContext';
import RouteErrorBoundary from './components/RouteErrorBoundary';

const SIDEBAR_EXPANDED = 240; // matches w-60
const SIDEBAR_COLLAPSED = 64; // matches w-16

function MainApp() {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const toggleCollapse = () => setIsCollapsed(v => !v);
  // const { open, data, dismiss } = useUpdateNotes();
  const globalSearch = useGlobalSearch();
  const shortcutsModal = useKeyboardShortcutsModal();
  
  // Widths should match your sidebar sizes (px)
  const NAV_OPEN_W = 240;        // expanded sidebar width
  const NAV_COLLAPSED_W = 72;    // collapsed sidebar width

  // Keep CSS vars --nav-w and --sidebar-w in sync with the sidebar state (and responsive)
  // Uses debounced resize handler to prevent excessive DOM updates
  const resizeTimeoutRef = useRef(null);

  const applyNavWidth = useCallback(() => {
    const isMobile = window.matchMedia("(max-width: 992px)").matches;
    const w = isMobile ? 0 : (isCollapsed ? NAV_COLLAPSED_W : NAV_OPEN_W);
    const sidebarW = isMobile ? 0 : (isCollapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED);
    document.documentElement.style.setProperty("--nav-w", `${w}px`);
    document.documentElement.style.setProperty("--sidebar-w", `${sidebarW}px`);
  }, [isCollapsed]);

  useEffect(() => {
    applyNavWidth();

    const debouncedResize = () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeTimeoutRef.current = setTimeout(applyNavWidth, 100);
    };

    window.addEventListener("resize", debouncedResize);
    return () => {
      window.removeEventListener("resize", debouncedResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [applyNavWidth]);

  return (
    <PrivacyProvider>
    <TourProvider>
      <div
        className={`app app-layout ${isCollapsed ? 'nav-mini' : 'nav-full'}`}
      >
        {/* Desktop Sidebar Navigation */}
        <Navbar
          isNavOpen={isNavOpen}
          closeNav={() => setIsNavOpen(false)}
          isCollapsed={isCollapsed}
          toggleCollapse={toggleCollapse}
          onSearchClick={globalSearch.open}
        />

        {/* Mobile Bottom Navigation */}
        <MobileNavBar />

        <div
          className="main-content-area transition-[margin-left] duration-300 ease-out pb-12 lg:pb-12 pb-20"
        >
          {/* Upgrade Banner - shows for free/starter users */}
          <UpgradeBanner />

          <RouteErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Protected routes - require active subscription */}
                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/inventory" element={<ProtectedRoute><InventoryPage /></ProtectedRoute>} />
                <Route path="/vault" element={<ProtectedRoute><ProFeaturePage feature={PRO_ONLY_FEATURES.VAULT}><VaultPage /></ProFeaturePage></ProtectedRoute>} />
                {/* eBay integration disabled - uncomment when ready for production */}
                {/* <Route path="/listings" element={<ProtectedRoute><ListingsPage /></ProtectedRoute>} /> */}
                <Route path="/sales" element={<ProtectedRoute><SalesPage /></ProtectedRoute>} />
                {/* A selected sale lives in the URL so it survives a
                    reload, can be linked to from the dashboard, and can be
                    shared. Declared before /sales/:saleId/item/:itemId,
                    which is more specific and still matches first. */}
                <Route path="/sales/:saleId" element={<ProtectedRoute><SalesPage /></ProtectedRoute>} />
                <Route path="/buyers" element={<ProtectedRoute><BuyersPage /></ProtectedRoute>} />
                {/* A selected buyer lives in the URL, same as /sales/:saleId —
                    survives a reload and can be linked to. */}
                <Route path="/buyers/:buyerId" element={<ProtectedRoute><BuyersPage /></ProtectedRoute>} />
                <Route path="/expenses" element={<ProtectedRoute><ExpensesPage /></ProtectedRoute>} />
                {/* Renewals archived — now accessed via drawer in ExpensesPage */}
                <Route path="/renewals" element={<Navigate to="/expenses" replace />} />
                <Route path="/product-scanner" element={<ProtectedRoute><ProductScannerPage /></ProtectedRoute>} />
                <Route path="/global-products-admin" element={<ProtectedRoute><GlobalProductsAdmin /></ProtectedRoute>} />
                <Route path="/admin-settings" element={<ProtectedRoute><AdminSettingsPage /></ProtectedRoute>} />
                {/* Old routes redirect to merged Orders & Tracking page */}
                <Route path="/packages" element={<Navigate to="/orders-tracking" replace />} />
                <Route path="/email-scraper" element={<Navigate to="/orders-tracking" replace />} />
                <Route path="/orders-tracking" element={<ProtectedRoute><OrdersTrackingPage /></ProtectedRoute>} />
                <Route path="/reports" element={<ProtectedRoute><ProFeaturePage feature={PRO_ONLY_FEATURES.REPORTS}><Reports /></ProFeaturePage></ProtectedRoute>} />
                <Route path="/view/:id" element={<ProtectedRoute><ViewItemPage /></ProtectedRoute>} />
                <Route path="/sales/:saleId/item/:itemId" element={<ProtectedRoute><ViewItemPage /></ProtectedRoute>} />
                <Route path="/view-vault-item/:id" element={<ProtectedRoute><ViewItemPage /></ProtectedRoute>} />
                {/* Unprotected routes - always accessible */}
                <Route path="/account" element={<AccountPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/gradient-playground" element={<GradientPlaygroundPage />} />
                {/* eBay integration disabled - uncomment when ready for production */}
                {/* <Route path="/settings/integrations/ebay/callback" element={<EbayCallbackPage />} /> */}
                <Route path="/archives" element={<ArchivesPage />} />
                {/* Root redirect - authenticated users landing on "/" go to dashboard */}
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                {/* 404 Not Found - must be last */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
          </RouteErrorBoundary>
          {/* <UpdateNotesModal open={open} data={data} onClose={dismiss} /> */}
          <Footer />
        </div>

        {/* Global Modals */}
        <GlobalSearchModal open={globalSearch.isOpen} onClose={globalSearch.close} />
        <KeyboardShortcutsModal open={shortcutsModal.isOpen} onClose={shortcutsModal.close} />
        <WelcomeModal />

        {/* Mobile Quick Actions FAB */}
        <MobileQuickActions />
      </div>
    </TourProvider>
    </PrivacyProvider>
  );
}

export default MainApp;